import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DigitalEmployee, EmployeeCategory, Industry } from '../types';
import { skillCategories } from '../data/skills';
import { digitalEmployees } from '../data';
import '../build-employee.css';

const BuildEmployeePage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<number>(1);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [employeeInfo, setEmployeeInfo] = useState({
    name: '',
    description: '',
    category: 'Developer' as EmployeeCategory,
    industries: [] as Industry[],
    availability: 'Weekdays, 9AM-5PM',
  });
  const [avatarOptions, setAvatarOptions] = useState<string[]>([]);
  const [selectedAvatar, setSelectedAvatar] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generatedEmployee, setGeneratedEmployee] = useState<DigitalEmployee | null>(null);

  // Generate random avatar options
  useEffect(() => {
    const options = [];
    for (let i = 0; i < 6; i++) {
      const topTypes = ['NoHair', 'Eyepatch', 'Hat', 'Hijab', 'Turban', 'WinterHat1', 'WinterHat2', 'WinterHat3', 'WinterHat4', 'LongHairBigHair', 'LongHairBob', 'LongHairBun', 'LongHairCurly', 'LongHairCurvy', 'LongHairDreads', 'LongHairFrida', 'LongHairFro', 'LongHairFroBand', 'LongHairNotTooLong', 'LongHairShavedSides', 'LongHairMiaWallace', 'LongHairStraight', 'LongHairStraight2', 'LongHairStraightStrand', 'ShortHairDreads01', 'ShortHairDreads02', 'ShortHairFrizzle', 'ShortHairShaggyMullet', 'ShortHairShortCurly', 'ShortHairShortFlat', 'ShortHairShortRound', 'ShortHairShortWaved', 'ShortHairSides', 'ShortHairTheCaesar', 'ShortHairTheCaesarSidePart'];
      const accessoriesTypes = ['Blank', 'Kurt', 'Prescription01', 'Prescription02', 'Round', 'Sunglasses', 'Wayfarers'];
      const hairColors = ['Auburn', 'Black', 'Blonde', 'BlondeGolden', 'Brown', 'BrownDark', 'PastelPink', 'Platinum', 'Red', 'SilverGray'];
      const facialHairTypes = ['Blank', 'BeardMedium', 'BeardLight', 'BeardMajestic', 'MoustacheFancy', 'MoustacheMagnum'];
      const clotheTypes = ['BlazerShirt', 'BlazerSweater', 'CollarSweater', 'GraphicShirt', 'Hoodie', 'Overall', 'ShirtCrewNeck', 'ShirtScoopNeck', 'ShirtVNeck'];
      const clotheColors = ['Black', 'Blue01', 'Blue02', 'Blue03', 'Gray01', 'Gray02', 'Heather', 'PastelBlue', 'PastelGreen', 'PastelOrange', 'PastelRed', 'PastelYellow', 'Pink', 'Red', 'White'];
      const eyeTypes = ['Close', 'Cry', 'Default', 'Dizzy', 'EyeRoll', 'Happy', 'Hearts', 'Side', 'Squint', 'Surprised', 'Wink', 'WinkWacky'];
      const eyebrowTypes = ['Angry', 'AngryNatural', 'Default', 'DefaultNatural', 'FlatNatural', 'RaisedExcited', 'RaisedExcitedNatural', 'SadConcerned', 'SadConcernedNatural', 'UnibrowNatural', 'UpDown', 'UpDownNatural'];
      const mouthTypes = ['Concerned', 'Default', 'Disbelief', 'Eating', 'Grimace', 'Sad', 'ScreamOpen', 'Serious', 'Smile', 'Tongue', 'Twinkle', 'Vomit'];
      const skinColors = ['Tanned', 'Yellow', 'Pale', 'Light', 'Brown', 'DarkBrown', 'Black'];

      const randomTopType = topTypes[Math.floor(Math.random() * topTypes.length)];
      const randomAccessoriesType = accessoriesTypes[Math.floor(Math.random() * accessoriesTypes.length)];
      const randomHairColor = hairColors[Math.floor(Math.random() * hairColors.length)];
      const randomFacialHairType = facialHairTypes[Math.floor(Math.random() * facialHairTypes.length)];
      const randomClotheType = clotheTypes[Math.floor(Math.random() * clotheTypes.length)];
      const randomClotheColor = clotheColors[Math.floor(Math.random() * clotheColors.length)];
      const randomEyeType = eyeTypes[Math.floor(Math.random() * eyeTypes.length)];
      const randomEyebrowType = eyebrowTypes[Math.floor(Math.random() * eyebrowTypes.length)];
      const randomMouthType = mouthTypes[Math.floor(Math.random() * mouthTypes.length)];
      const randomSkinColor = skinColors[Math.floor(Math.random() * skinColors.length)];

      const avatarUrl = `https://avataaars.io/?avatarStyle=Circle&topType=${randomTopType}&accessoriesType=${randomAccessoriesType}&hairColor=${randomHairColor}&facialHairType=${randomFacialHairType}&clotheType=${randomClotheType}&clotheColor=${randomClotheColor}&eyeType=${randomEyeType}&eyebrowType=${randomEyebrowType}&mouthType=${randomMouthType}&skinColor=${randomSkinColor}`;
      options.push(avatarUrl);
    }
    setAvatarOptions(options);
    setSelectedAvatar(options[0]);
  }, []);

  const handleSkillToggle = (skill: string) => {
    if (selectedSkills.includes(skill)) {
      setSelectedSkills(selectedSkills.filter(s => s !== skill));
    } else {
      if (selectedSkills.length < 10) {
        setSelectedSkills([...selectedSkills, skill]);
      }
    }
  };

  const handleIndustryToggle = (industry: Industry) => {
    if (employeeInfo.industries.includes(industry)) {
      setEmployeeInfo({
        ...employeeInfo,
        industries: employeeInfo.industries.filter(i => i !== industry)
      });
    } else {
      if (employeeInfo.industries.length < 3) {
        setEmployeeInfo({
          ...employeeInfo,
          industries: [...employeeInfo.industries, industry]
        });
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEmployeeInfo({
      ...employeeInfo,
      [name]: value
    });
  };

  const handleAvatarSelect = (avatar: string) => {
    setSelectedAvatar(avatar);
  };

  const handleGenerateEmployee = () => {
    setIsGenerating(true);
    
    // Simulate AI generation process
    setTimeout(() => {
      const newId = (digitalEmployees.length + 1).toString();
      
      const newEmployee: DigitalEmployee = {
        id: newId,
        name: employeeInfo.name,
        description: employeeInfo.description,
        avatar: selectedAvatar,
        skills: selectedSkills,
        hourlyRate: 20, // Fixed rate as per the new pricing model
        availability: employeeInfo.availability,
        category: employeeInfo.category,
        industries: employeeInfo.industries
      };
      
      setGeneratedEmployee(newEmployee);
      setIsGenerating(false);
    }, 2000);
  };

  const handleAddToWorkspace = () => {
    if (generatedEmployee) {
      // In a real application, this would add the employee to the user's workspace
      // For this demo, we'll just navigate to the employees page
      navigate('/employees');
    }
  };

  const nextStep = () => {
    if (step === 1 && selectedSkills.length === 0) {
      alert('Please select at least one skill');
      return;
    }
    
    if (step === 2 && (!employeeInfo.name || !employeeInfo.description || employeeInfo.industries.length === 0)) {
      alert('Please fill in all required fields');
      return;
    }
    
    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const renderStepOne = () => (
    <div className="build-step">
      <h2>Step 1: Select Skills</h2>
      <p className="build-instruction">Choose up to 10 skills for your AI employee</p>
      
      <div className="skills-counter">
        Selected: <span className="skills-count">{selectedSkills.length}</span>/10
      </div>
      
      <div className="skill-categories">
        {skillCategories.map((category, index) => (
          <div key={index} className="skill-category">
            <h3>{category.category}</h3>
            <div className="skill-grid">
              {category.skills.map((skill, skillIndex) => (
                <div 
                  key={skillIndex} 
                  className={`skill-chip ${selectedSkills.includes(skill) ? 'selected' : ''} ${selectedSkills.length >= 10 && !selectedSkills.includes(skill) ? 'disabled' : ''}`}
                  onClick={() => handleSkillToggle(skill)}
                >
                  {skill}
                  {selectedSkills.includes(skill) && <span className="skill-check">✓</span>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      <div className="build-navigation">
        <button className="next-button" onClick={nextStep}>
          Next <span className="button-icon">→</span>
        </button>
      </div>
    </div>
  );

  const renderStepTwo = () => (
    <div className="build-step">
      <h2>Step 2: Basic Information</h2>
      <p className="build-instruction">Provide basic information for your AI employee</p>
      
      <div className="form-group">
        <label htmlFor="name">Name <span className="required">*</span></label>
        <input 
          type="text" 
          id="name" 
          name="name" 
          value={employeeInfo.name} 
          onChange={handleInputChange} 
          placeholder="Enter a name for your AI employee"
          required
        />
      </div>
      
      <div className="form-group">
        <label htmlFor="description">Description <span className="required">*</span></label>
        <textarea 
          id="description" 
          name="description" 
          value={employeeInfo.description} 
          onChange={handleInputChange} 
          placeholder="Describe what your AI employee does"
          rows={4}
          required
        />
      </div>
      
      <div className="form-group">
        <label htmlFor="category">Category <span className="required">*</span></label>
        <select 
          id="category" 
          name="category" 
          value={employeeInfo.category} 
          onChange={handleInputChange}
          required
        >
          <option value="Developer">Developer</option>
          <option value="HR">HR</option>
          <option value="PMO">PMO</option>
          <option value="Personal Assistant">Personal Assistant</option>
          <option value="RFP Developer">RFP Developer</option>
        </select>
      </div>
      
      <div className="form-group">
        <label>Industries <span className="required">*</span></label>
        <p className="form-help">Select up to 3 industries</p>
        <div className="industry-options">
          {['Real Estate', 'Education', 'Transportation', 'Hospitality', 'Finance'].map((industry, index) => (
            <div 
              key={index} 
              className={`industry-chip ${employeeInfo.industries.includes(industry as Industry) ? 'selected' : ''}`}
              onClick={() => handleIndustryToggle(industry as Industry)}
            >
              {industry}
              {employeeInfo.industries.includes(industry as Industry) && <span className="industry-check">✓</span>}
            </div>
          ))}
        </div>
      </div>
      
      <div className="form-group">
        <label htmlFor="availability">Availability</label>
        <input 
          type="text" 
          id="availability" 
          name="availability" 
          value={employeeInfo.availability} 
          onChange={handleInputChange} 
          placeholder="e.g., Weekdays, 9AM-5PM"
        />
      </div>
      
      <div className="build-navigation">
        <button className="back-button" onClick={prevStep}>
          <span className="button-icon">←</span> Back
        </button>
        <button className="next-button" onClick={nextStep}>
          Next <span className="button-icon">→</span>
        </button>
      </div>
    </div>
  );

  const renderStepThree = () => (
    <div className="build-step">
      <h2>Step 3: Choose Avatar</h2>
      <p className="build-instruction">Select an avatar for your AI employee</p>
      
      <div className="avatar-grid">
        {avatarOptions.map((avatar, index) => (
          <div 
            key={index} 
            className={`avatar-option ${selectedAvatar === avatar ? 'selected' : ''}`}
            onClick={() => handleAvatarSelect(avatar)}
          >
            <img src={avatar} alt={`Avatar option ${index + 1}`} />
            {selectedAvatar === avatar && <div className="avatar-selected-indicator">✓</div>}
          </div>
        ))}
      </div>
      
      <div className="avatar-refresh">
        <button 
          className="refresh-button"
          onClick={() => {
            // Generate new avatar options
            const options = [];
            for (let i = 0; i < 6; i++) {
              const topTypes = ['NoHair', 'Eyepatch', 'Hat', 'Hijab', 'Turban', 'WinterHat1', 'WinterHat2', 'WinterHat3', 'WinterHat4', 'LongHairBigHair', 'LongHairBob', 'LongHairBun', 'LongHairCurly', 'LongHairCurvy', 'LongHairDreads', 'LongHairFrida', 'LongHairFro', 'LongHairFroBand', 'LongHairNotTooLong', 'LongHairShavedSides', 'LongHairMiaWallace', 'LongHairStraight', 'LongHairStraight2', 'LongHairStraightStrand', 'ShortHairDreads01', 'ShortHairDreads02', 'ShortHairFrizzle', 'ShortHairShaggyMullet', 'ShortHairShortCurly', 'ShortHairShortFlat', 'ShortHairShortRound', 'ShortHairShortWaved', 'ShortHairSides', 'ShortHairTheCaesar', 'ShortHairTheCaesarSidePart'];
              const accessoriesTypes = ['Blank', 'Kurt', 'Prescription01', 'Prescription02', 'Round', 'Sunglasses', 'Wayfarers'];
              const hairColors = ['Auburn', 'Black', 'Blonde', 'BlondeGolden', 'Brown', 'BrownDark', 'PastelPink', 'Platinum', 'Red', 'SilverGray'];
              const facialHairTypes = ['Blank', 'BeardMedium', 'BeardLight', 'BeardMajestic', 'MoustacheFancy', 'MoustacheMagnum'];
              const clotheTypes = ['BlazerShirt', 'BlazerSweater', 'CollarSweater', 'GraphicShirt', 'Hoodie', 'Overall', 'ShirtCrewNeck', 'ShirtScoopNeck', 'ShirtVNeck'];
              const clotheColors = ['Black', 'Blue01', 'Blue02', 'Blue03', 'Gray01', 'Gray02', 'Heather', 'PastelBlue', 'PastelGreen', 'PastelOrange', 'PastelRed', 'PastelYellow', 'Pink', 'Red', 'White'];
              const eyeTypes = ['Close', 'Cry', 'Default', 'Dizzy', 'EyeRoll', 'Happy', 'Hearts', 'Side', 'Squint', 'Surprised', 'Wink', 'WinkWacky'];
              const eyebrowTypes = ['Angry', 'AngryNatural', 'Default', 'DefaultNatural', 'FlatNatural', 'RaisedExcited', 'RaisedExcitedNatural', 'SadConcerned', 'SadConcernedNatural', 'UnibrowNatural', 'UpDown', 'UpDownNatural'];
              const mouthTypes = ['Concerned', 'Default', 'Disbelief', 'Eating', 'Grimace', 'Sad', 'ScreamOpen', 'Serious', 'Smile', 'Tongue', 'Twinkle', 'Vomit'];
              const skinColors = ['Tanned', 'Yellow', 'Pale', 'Light', 'Brown', 'DarkBrown', 'Black'];

              const randomTopType = topTypes[Math.floor(Math.random() * topTypes.length)];
              const randomAccessoriesType = accessoriesTypes[Math.floor(Math.random() * accessoriesTypes.length)];
              const randomHairColor = hairColors[Math.floor(Math.random() * hairColors.length)];
              const randomFacialHairType = facialHairTypes[Math.floor(Math.random() * facialHairTypes.length)];
              const randomClotheType = clotheTypes[Math.floor(Math.random() * clotheTypes.length)];
              const randomClotheColor = clotheColors[Math.floor(Math.random() * clotheColors.length)];
              const randomEyeType = eyeTypes[Math.floor(Math.random() * eyeTypes.length)];
              const randomEyebrowType = eyebrowTypes[Math.floor(Math.random() * eyebrowTypes.length)];
              const randomMouthType = mouthTypes[Math.floor(Math.random() * mouthTypes.length)];
              const randomSkinColor = skinColors[Math.floor(Math.random() * skinColors.length)];

              const avatarUrl = `https://avataaars.io/?avatarStyle=Circle&topType=${randomTopType}&accessoriesType=${randomAccessoriesType}&hairColor=${randomHairColor}&facialHairType=${randomFacialHairType}&clotheType=${randomClotheType}&clotheColor=${randomClotheColor}&eyeType=${randomEyeType}&eyebrowType=${randomEyebrowType}&mouthType=${randomMouthType}&skinColor=${randomSkinColor}`;
              options.push(avatarUrl);
            }
            setAvatarOptions(options);
            setSelectedAvatar(options[0]);
          }}
        >
          Generate New Avatars
        </button>
      </div>
      
      <div className="build-navigation">
        <button className="back-button" onClick={prevStep}>
          <span className="button-icon">←</span> Back
        </button>
        <button className="generate-button" onClick={handleGenerateEmployee}>
          Generate AI Employee <span className="button-icon">✨</span>
        </button>
      </div>
    </div>
  );

  const renderGeneratingStep = () => (
    <div className="build-step generating-step">
      <h2>Generating Your AI Employee</h2>
      <div className="generating-animation">
        <div className="generating-spinner"></div>
      </div>
      <p className="generating-text">Please wait while we generate your AI employee...</p>
      <p className="generating-subtext">This may take a few moments</p>
    </div>
  );

  const renderResultStep = () => (
    <div className="build-step result-step">
      <h2>Your AI Employee is Ready!</h2>
      
      {generatedEmployee && (
        <div className="employee-result">
          <div className="employee-result-avatar">
            <img src={generatedEmployee.avatar} alt={generatedEmployee.name} />
          </div>
          
          <div className="employee-result-details">
            <h3>{generatedEmployee.name}</h3>
            <p className="employee-result-description">{generatedEmployee.description}</p>
            
            <div className="employee-result-info">
              <div className="employee-result-info-item">
                <span className="info-label">Category:</span>
                <span className="info-value">{generatedEmployee.category}</span>
              </div>
              
              <div className="employee-result-info-item">
                <span className="info-label">Industries:</span>
                <span className="info-value">{generatedEmployee.industries.join(', ')}</span>
              </div>
              
              <div className="employee-result-info-item">
                <span className="info-label">Availability:</span>
                <span className="info-value">{generatedEmployee.availability}</span>
              </div>
              
              <div className="employee-result-info-item">
                <span className="info-label">Price:</span>
                <span className="info-value">$20/month per seat</span>
              </div>
            </div>
            
            <div className="employee-result-skills">
              <h4>Skills</h4>
              <div className="employee-skills-list">
                {generatedEmployee.skills.map((skill, index) => (
                  <div key={index} className="employee-skill-chip">
                    {skill}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="result-actions">
        <button className="add-to-workspace-button" onClick={handleAddToWorkspace}>
          Add to Workspace <span className="button-icon">+</span>
        </button>
        <button className="start-over-button" onClick={() => {
          setStep(1);
          setSelectedSkills([]);
          setEmployeeInfo({
            name: '',
            description: '',
            category: 'Developer' as EmployeeCategory,
            industries: [] as Industry[],
            availability: 'Weekdays, 9AM-5PM',
          });
          setGeneratedEmployee(null);
        }}>
          Start Over <span className="button-icon">↺</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="build-employee-page">
      <div className="build-employee-header">
        <h1>Build Your AI Employee</h1>
        <p className="build-subtitle">Create a custom AI employee with the skills you need</p>
      </div>
      
      {!isGenerating && !generatedEmployee && (
        <div className="build-progress">
          <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <div className="step-number">1</div>
            <div className="step-label">Select Skills</div>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-label">Basic Information</div>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`}>
            <div className="step-number">3</div>
            <div className="step-label">Choose Avatar</div>
          </div>
        </div>
      )}
      
      <div className="build-content">
        {isGenerating && renderGeneratingStep()}
        {!isGenerating && generatedEmployee && renderResultStep()}
        {!isGenerating && !generatedEmployee && step === 1 && renderStepOne()}
        {!isGenerating && !generatedEmployee && step === 2 && renderStepTwo()}
        {!isGenerating && !generatedEmployee && step === 3 && renderStepThree()}
      </div>
    </div>
  );
};

export default BuildEmployeePage;
